// Avatar model (wiki/feature-user-profile.md AC-UP-3). A user's avatar is
// EITHER a bundled stock-avatar id (`stock:<emoji>`) OR an uploaded
// object-storage key (under the `avatars/` prefix). Stock avatars are rendered
// as a big emoji in a circle — zero image assets, deterministic + playful.

/** The funny stock-avatar emoji set. Each id is `stock:<emoji>`. */
export const STOCK_AVATARS: readonly string[] = [
  '🦊',
  '🐙',
  '🦉',
  '🐸',
  '🦄',
  '🐼',
  '🦜',
  '🐝',
  '🦖',
  '🐧',
  '🐳',
  '🦩',
];

const STOCK_PREFIX = 'stock:';
const UPLOAD_PREFIX = 'avatars/';

/** The stock-avatar id for a given emoji (e.g. `stock:🦊`). */
export function stockAvatarId(emoji: string): string {
  return `${STOCK_PREFIX}${emoji}`;
}

/** True when the avatar value is an uploaded object-storage key. */
export function isUploadedAvatar(avatar: string | null | undefined): boolean {
  return typeof avatar === 'string' && avatar.startsWith(UPLOAD_PREFIX);
}

/** True when the avatar value is a bundled stock-avatar id. */
export function isStockAvatar(avatar: string | null | undefined): boolean {
  return typeof avatar === 'string' && avatar.startsWith(STOCK_PREFIX);
}

/** The emoji for a stock-avatar id, or null when not a stock avatar. */
export function stockEmoji(avatar: string | null | undefined): string | null {
  if (!isStockAvatar(avatar)) return null;
  return (avatar as string).slice(STOCK_PREFIX.length);
}

/**
 * Picks a deterministic funny stock avatar for a brand-new account from its id,
 * so the same account always shows the same default until they change it. Used
 * client-side as the display fallback when the server has assigned no avatar yet.
 */
export function defaultStockAvatarFor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return stockAvatarId(STOCK_AVATARS[hash % STOCK_AVATARS.length]);
}
